<template>
  <div
    class="overflow-hidden rounded-[20px] border border-gray-border bg-white px-4 py-3 text-[15px] leading-[1.4] font-helvetica-400 sm:px-5 xl:px-6 xl:text-[16px]"
  >
    <div class="flex min-w-0 flex-col gap-3 min-[1280px]:flex-row min-[1280px]:items-center min-[1280px]:gap-4">
      <div class="min-w-0 flex-1 text-gray-dark">
        <span class="block truncate">
          Найдено товаров:
          <span class="font-helvetica-700 text-gray-dark whitespace-nowrap">{{ count }}</span>
        </span>
      </div>

      <button
        type="button"
        class="flex h-14 items-center gap-3 rounded-[20px] border border-gray-border bg-white px-4 text-left text-gray-dark min-[1280px]:hidden"
        @click="mobileSortOpen = true"
      >
        <span class="text-[16px] text-extra-text-color">Сначала</span>
        <span class="ml-auto min-w-0 truncate text-[16px] font-helvetica-700 text-orange">{{ mobileSortLabel }}</span>
        <SvgoIconComponentFIconFilterarrow class="h-[7px] w-[11px] shrink-0 text-orange"></SvgoIconComponentFIconFilterarrow>
      </button>

      <div class="ml-auto hidden min-[1280px]:block min-w-0 max-w-[62%] shrink">
        <UPopover v-model:open="sortOpen" :content="{ side: 'bottom', align: 'start', sideOffset: 8 }">
          <button
            type="button"
            class="flex w-full min-w-0 max-w-full items-center justify-end gap-2 text-right text-gray-dark transition-colors focus:outline-none"
          >
            <span class="shrink-0 whitespace-nowrap">Сортировка:</span>
            <span class="min-w-0 truncate font-helvetica-700 text-orange" :title="sortLabel">{{ sortLabel }}</span>
            <SvgoIconComponentFIconFilterarrow class="h-[7px] w-[11px] shrink-0 text-orange transition-transform duration-200" :class="sortOpen ? 'rotate-180' : ''"></SvgoIconComponentFIconFilterarrow>
          </button>

          <template #content="{ close }">
            <div class="w-[280px] rounded-[12px] border border-gray-border bg-white p-4 shadow-sm">
              <div class="space-y-3">
                <button
                  v-for="option in sortOptionsList"
                  :key="option.value"
                  type="button"
                  class="flex w-full items-center gap-3 text-left text-[16px] leading-[22px] text-gray-dark transition-colors hover:text-orange"
                  @click="selectSort(option.value, close)"
                >
                  <span
                    class="h-5 w-5 rounded-full border bg-white"
                    :class="isSortSelected(option.value) ? 'border-[5px] border-orange' : 'border-gray-border'"
                  />
                  <span>{{ option.label }}</span>
                </button>
              </div>
            </div>
          </template>
        </UPopover>
      </div>
    </div>

    <div
      v-if="mobileSortOpen"
      class="fixed inset-0 z-[1250] bg-black/30 min-[1280px]:hidden"
      @click.self="mobileSortOpen = false"
    >
      <section
        class="absolute inset-x-0 bottom-0 rounded-t-[28px] bg-white px-5 pb-[calc(24px+env(safe-area-inset-bottom))] pt-5 shadow-[0_-12px_32px_rgb(0_0_0_/_0.12)]"
      >
        <div class="mx-auto flex w-full max-w-[var(--layout-mobile-max-width)] flex-col gap-4">
          <div class="relative flex items-center justify-center">
            <p class="text-[26px] leading-none font-helvetica-700 text-gray-dark">
              Показать сначала
            </p>
            <button
              type="button"
              class="absolute right-0 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-border bg-white-gray text-gray-dark"
              @click="mobileSortOpen = false"
              aria-label="Закрыть сортировку"
            >
              <span class="absolute h-[1.5px] w-4 rotate-45 rounded-full bg-current"></span>
              <span class="absolute h-[1.5px] w-4 -rotate-45 rounded-full bg-current"></span>
            </button>
          </div>

          <div class="space-y-3">
            <button
              v-for="option in sortOptionsList"
              :key="option.value"
              type="button"
              class="flex w-full items-center gap-3 text-left text-[18px] leading-[24px] text-gray-dark"
              @click="selectMobileSort(option.value)"
            >
              <span
                class="inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-border bg-white"
              >
                <span
                  v-if="isSortSelected(option.value)"
                  class="h-2.5 w-2.5 rounded-full bg-orange"
                />
              </span>
              <span>{{ option.label }}</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

type Option = { label: string; value: string };

const defaultSortOptions: Option[] = [
  { label: "Сначала недорогие", value: "cheap-first" },
  { label: "Сначала дорогие", value: "expensive-first" },
  { label: "По наименованию (от А до Я)", value: "name-asc" },
  { label: "По наименованию (от Я до А)", value: "name-desc" },
];

const defaultGroupOptions: Option[] = [
  { label: "Отсутствует", value: "none" },
  { label: "По производителю", value: "brand" },
  { label: "По модели", value: "model" },
  { label: "По наличию", value: "stock" },
];

const props = defineProps<{
  count?: number | string;
  sort?: string;
  group?: string;
  sortOptions?: Option[];
  groupOptions?: Option[];
}>();

const emit = defineEmits<{
  (e: "update:sort", value: string): void;
  (e: "update:group", value: string): void;
}>();

const sortOptionsList = computed<Option[]>(
  () => props.sortOptions?.length ? props.sortOptions : defaultSortOptions
);
const groupOptionsList = computed<Option[]>(
  () => props.groupOptions?.length ? props.groupOptions : defaultGroupOptions
);

const sortValue = ref(props.sort ?? sortOptionsList.value[0]?.value ?? "");
const groupValue = ref(props.group ?? groupOptionsList.value[0]?.value ?? "");
const sortOpen = ref(false);
const groupOpen = ref(false);
const mobileSortOpen = ref(false);

watch(
  () => props.sort,
  (val) => {
    if (val && val !== sortValue.value) {
      sortValue.value = val;
    }
  }
);

watch(
  () => props.group,
  (val) => {
    if (val && val !== groupValue.value) {
      groupValue.value = val;
    }
  }
);

const sortLabel = computed(
  () => sortOptionsList.value.find((o) => o.value === sortValue.value)?.label ?? ""
);
const mobileSortLabel = computed(() => {
  const option = sortOptionsList.value.find((item) => item.value === sortValue.value);
  if (!option) return "";

  return (
    {
      "cheap-first": "Дешевые",
      "expensive-first": "Дорогие",
      "name-asc": "По названию",
      "name-desc": "С конца",
    }[option.value] ?? option.label
  );
});
const groupLabel = computed(
  () => groupOptionsList.value.find((o) => o.value === groupValue.value)?.label ?? ""
);

const isSortSelected = (value: string) => sortValue.value === value;
const isGroupSelected = (value: string) => groupValue.value === value;

const selectSort = (value: string, close: () => void) => {
  sortValue.value = value;
  emit("update:sort", value);
  close();
};

const selectMobileSort = (value: string) => {
  sortValue.value = value;
  emit("update:sort", value);
  mobileSortOpen.value = false;
};

const selectGroup = (value: string, close: () => void) => {
  groupValue.value = value;
  emit("update:group", value);
  close();
};

const count = computed(() => props.count ?? 0);
</script>
